import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { BoxesIcon, PlusIcon, StickyNoteIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { ChassisCodeList } from "@/components/app/chassis-codes";
import { CurrentVehicleNotice } from "@/components/app/current-vehicle";
import { EmptyState } from "@/components/app/empty-state";
import { Page, PageHeader } from "@/components/app/page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/misc";
import { ServiceIntervalLabel } from "@/features/templates/service-interval";
import { TemplateSheet } from "@/features/templates/template-sheet";
import { useCurrentVehicle } from "@/lib/current-vehicle";
import { useFormat } from "@/lib/format";
import { searchText } from "@/lib/search";
import { useTRPC } from "@/lib/trpc";

export const Route = createFileRoute("/templates/")({
  validateSearch: z.object({ new: searchText }),
  component: TemplatesPage,
});

function TemplatesPage() {
  const { t } = useTranslation();
  const trpc = useTRPC();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const format = useFormat();
  const { vehicleId } = useCurrentVehicle();
  const { data: templates, isLoading } = useQuery(trpc.templates.list.queryOptions({ vehicleId }));
  const openNew = () => void navigate({ search: { new: "1" } });

  return (
    <Page>
      <PageHeader
        title={t("templates.title")}
        actions={
          <Button onClick={openNew}>
            <PlusIcon />
            {t("templates.new")}
          </Button>
        }
      />
      <CurrentVehicleNotice />
      {isLoading ? (
        <Skeleton className="h-48 rounded-xl" />
      ) : !templates?.length ? (
        <EmptyState
          icon={StickyNoteIcon}
          title={t("templates.emptyTitle")}
          body={t("templates.emptyBody")}
          action={
            <Button onClick={openNew}>
              <PlusIcon />
              {t("templates.new")}
            </Button>
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((tpl) => (
            <Link
              key={tpl.id}
              to="/templates/$templateId"
              params={{ templateId: tpl.id }}
              className="group flex flex-col gap-3 rounded-xl border border-border bg-surface p-4 shadow-xs transition hover:-translate-y-0.5 hover:shadow-md"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex size-9 items-center justify-center rounded-lg bg-brand-soft text-brand-fg">
                  <StickyNoteIcon className="size-4" />
                </div>
                <Badge tone="outline">{t(`workOrderType.${tpl.type}`)}</Badge>
              </div>
              <div>
                <h2 className="font-semibold tracking-tight">{tpl.title}</h2>
                {tpl.description ? (
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                    {tpl.description.replace(/[*#_>`]/g, "")}
                  </p>
                ) : null}
                {tpl.interval || tpl.chassisCodes.length ? (
                  <div className="mt-2 flex flex-col gap-1 text-xs text-muted-foreground">
                    <ServiceIntervalLabel interval={tpl.interval} />
                    <ChassisCodeList codes={tpl.chassisCodes} max={5} />
                  </div>
                ) : null}
              </div>
              <div className="mt-auto flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
                <span className="flex min-w-0 items-center gap-1">
                  <BoxesIcon className="size-3.5 shrink-0" />
                  <span className="truncate">
                    {t("workOrders.partsCount", { count: tpl.partCount })}
                    {tpl.estimate.priced ? ` · ${format.moneyRange(tpl.estimate)}` : ""}
                  </span>
                </span>
                <span>
                  {tpl.workOrderCount
                    ? t("templates.used", { count: tpl.workOrderCount })
                    : t("templates.neverUsed")}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}
      <TemplateSheet
        open={search.new === "1"}
        onOpenChange={(open) => void navigate({ search: open ? { new: "1" } : {} })}
        onSaved={(id) =>
          void navigate({ to: "/templates/$templateId", params: { templateId: id }, search: {} })
        }
      />
    </Page>
  );
}
